/** Core Modules */
import { APP_INITIALIZER, NgModule, isDevMode } from '@angular/core';
import { HttpClientModule, HttpClient, HTTP_INTERCEPTORS } from '@angular/common/http';
import { TranslateModule, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations'; // this is needed!
/** App Module */
import { AppComponent } from './app.component';
/* Interceptor Modules */
import { MissingTranslationHandler } from '@ngx-translate/core';
import { RemoveNullInterceptor } from '@interceptors/removenull.interceptor';
import { CommonInterceptor } from '@interceptors/common.interceptor';
import { UnknownError } from '@service/translator/missing-handler';
import { routes } from '@routes/routes';
import { mobileRoutes } from '@routes/mobile.routes';
import { Router } from '@angular/router';
import { DeviceDetectorService } from 'ngx-device-detector';
import { ConfigsService } from '@service/rest/config/configs.service';
import { TranslationLoaderService } from '@service/translator/translateLoader.service';
import { AppInitConfigService } from '@service/rest/config/app-init-config.service';
import { ThemeService } from '@service/theme.service';
import { ReadPaneService } from '@service/read-pane.service';
import { ServiceWorkerModule } from '@angular/service-worker';
import { AuthInterceptor } from '@interceptors/auth.interceptor';
import { AppRoutingModule } from './app-routing.module';
import { SharedModule } from '@modules/shared/shared.module';
import { RefreshTokenService } from '@service/rest/refresh-token/refresh-token.service';
import { LocalSettingsKey } from '@shared/types/local-settings';

export function loadTheme(themeService: ThemeService) {
  return () => {
    if (themeService.isActiveThemeDark) {
      themeService.applyDarkTheme();
    } else {
      themeService.applyLightTheme();
    }
  };
}

export function loadReadPaneTemplate(readPaneService: ReadPaneService) {
  return () => readPaneService.applyReadPaneTemplate();
}

export function loadTranslations(translateService: TranslateService): () => Promise<void> {
  return () => {
    const language = localStorage.getItem(LocalSettingsKey.TRANSLATE) || 'tr';
    translateService.setDefaultLang(language);
    translateService.use(language);
    return new Promise<void>(resolve => {
      translateService.onLangChange.subscribe(() => {
        resolve();
      });
    });
  };
}

export function loadConfigurations(
  appLoadService: AppInitConfigService,
  configsService: ConfigsService,
  refreshTokenService: RefreshTokenService
): () => Promise<any> {
  return () => {
    return appLoadService.init().then(() => {
      return new Promise<void>(resolve => {
        configsService.getConfigs().then(() => {
          resolve();
        });
      }).then(() => {
        return refreshTokenService.checkAccessTokenForInit().then();
      });
    });
  };
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    HttpClientModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    SharedModule.forRoot(),
    TranslateModule.forRoot({
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: UnknownError,
      },
      loader: {
        provide: TranslateLoader,
        useClass: TranslationLoaderService,
        deps: [HttpClient],
      },
    }),
    ServiceWorkerModule.register('sw.js', {
      enabled: !isDevMode(),
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RemoveNullInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CommonInterceptor,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: loadTheme,
      deps: [ThemeService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: loadReadPaneTemplate,
      deps: [ReadPaneService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: loadTranslations,
      deps: [TranslateService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: loadConfigurations,
      deps: [AppInitConfigService, ConfigsService, RefreshTokenService],
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(
    private router: Router,
    private deviceDetectorService: DeviceDetectorService
  ) {
    if (!this.deviceDetectorService.isMobile()) {
      this.router.resetConfig(routes);
    } else {
      this.router.resetConfig(mobileRoutes);
    }
  }
}
